<template>
  <v-dialog :value="open" persistent fullscreen content-class="dialogo-instalar">
    <v-container fill-height fluid class="bg_install" :class="'bg_install--' + tema">
      <div class="instalar-caja">
        <img class="instalar-logo" src="/assets/images/brand/white-horizontal-logo.svg" alt="Nubempresa">

        <ValidationObserver v-slot="{ invalid }" ref="form">
          <v-card class="auth-glass instalar-tarjeta">
            <div class="d-flex justify-end" v-if="conTutorial">
              <v-btn text small color="success" class="px-2">
                <v-icon small left>mdi-play-circle-outline</v-icon> Tutorial
              </v-btn>
            </div>

            <div class="text-center mb-5">
              <div class="instalar-icono mx-auto mb-3">
                <v-icon size="34" color="success">{{ icono }}</v-icon>
              </div>
              <h1 class="instalar-titulo success--text">{{ titulo }}</h1>
              <p class="grey--text text--darken-1 mb-0">{{ subtitulo }}</p>
            </div>

            <slot name="avisos"></slot>
            <slot></slot>
            <slot name="nota"></slot>

            <div class="mt-5">
              <slot name="acciones" :invalid="invalid"></slot>
            </div>
          </v-card>
        </ValidationObserver>
      </div>
    </v-container>
  </v-dialog>
</template>

<script>
/**
 * Marco de las pantallas para instalar un equipo (caja, meseros, cocina): fondo, logo, tarjeta con
 * título y validación. Los campos van en el espacio por defecto, los botones en `acciones`
 * (recibe `invalid`), y `avisos` / `nota` quedan arriba y abajo de los campos.
 */
export default {
  name: "InstalarPantalla",
  props: {
    open: { type: Boolean, default: false },
    titulo: { type: String, required: true },
    subtitulo: { type: String, default: "" },
    icono: { type: String, default: "mdi-point-of-sale" },
    // Cada equipo tiene su color para que no se confundan: caja (azul), meseros (violeta) y cocina (vino).
    tema: { type: String, default: "caja" },
    // El botón «Tutorial» (por ahora sin video); abrir turno no lo lleva.
    conTutorial: { type: Boolean, default: true }
  }
};
</script>

<style>
a {
  text-decoration: none;
}

/* Las barras de desplazamiento son azules en todo el POS (index.html) y se veían como una franja a la
   derecha: la de la página de atrás y la del propio diálogo. */
html:has(.dialogo-instalar.v-dialog--active) {
  overflow-y: hidden !important;
}

.dialogo-instalar {
  scrollbar-width: none;
}

.dialogo-instalar::-webkit-scrollbar {
  display: none;
}

/* El que se desplaza es el diálogo de adentro, no `.dialogo-instalar`: con la ventana baja la pantalla se
   queda más alta que ella y hay que poder bajar hasta «Cerrar sesión». Barra delgada y discreta. */
.dialogo-instalar > .v-dialog {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.4) transparent;
}

.dialogo-instalar > .v-dialog::-webkit-scrollbar {
  width: 6px;
}

.dialogo-instalar > .v-dialog::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 3px;
}

.instalar-caja {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  /* Aire abajo: el diálogo a pantalla completa le quita el relleno al contenedor y el último botón quedaba pegado al borde. */
  padding: 16px 0 28px;
}

.instalar-logo {
  display: block;
  width: 64%;
  max-width: 260px;
  margin: 0 auto 20px;
}

.instalar-tarjeta {
  padding: 16px 20px 24px;
}

.instalar-icono {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 175, 80, 0.14);
}

.instalar-titulo {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 6px;
}

.instalar-aviso {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  background: rgba(76, 175, 80, 0.1);
}

.instalar-aviso--copia {
  flex-direction: column;
  align-items: flex-start;
  background: rgba(255, 152, 0, 0.12);
}

@media (min-width: 600px) {
  .instalar-tarjeta {
    padding: 20px 32px 32px;
  }
}

/* Mismo fondo que el login: azul marino apagado, con el verde de acento y el símbolo de la nube detrás. */
.bg_install {
  position: relative;
  overflow: hidden;
  height: auto !important;
  min-height: 100% !important;
  background-color: #0b1f33 !important;
  background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1.5px),
    radial-gradient(circle at 12% 18%, rgba(76, 175, 80, 0.14), transparent 42%),
    radial-gradient(circle at 88% 85%, rgba(36, 169, 225, 0.34), transparent 52%),
    linear-gradient(135deg, #0b1f33 0%, #0d2d4a 50%, #0e3a5c 100%) !important;
  background-size: 26px 26px, auto, auto, auto !important;
  background-position: 0 0, center, center, center !important;
  background-repeat: repeat, no-repeat, no-repeat, no-repeat !important;
}

.bg_install::before {
  content: "";
  position: absolute;
  right: -8%;
  bottom: -14%;
  width: min(640px, 60vw);
  aspect-ratio: 1;
  background: url("/assets/images/brand/white-icon-logo.svg") no-repeat center / contain;
  opacity: 0.06;
  pointer-events: none;
}

.bg_install .instalar-caja {
  position: relative;
}

/* Ventana baja (portátiles pequeños, la app sin maximizar): se compacta para que quepa sin tanto desplazamiento. */
@media (max-height: 820px) {
  .instalar-caja {
    padding: 8px 0 20px;
  }

  .instalar-logo {
    width: 46%;
    max-width: 180px;
    margin-bottom: 10px;
  }

  .instalar-tarjeta {
    padding: 12px 20px 16px;
  }

  .instalar-icono {
    width: 48px;
    height: 48px;
  }

  .instalar-titulo {
    font-size: 1.35rem;
  }

  .instalar-tarjeta .v-input {
    margin-top: 0;
    padding-top: 0;
  }

  .instalar-tarjeta .v-text-field__details {
    margin-bottom: 0;
    min-height: 14px;
  }

  .instalar-tarjeta .v-input__slot {
    margin-bottom: 2px;
  }

  .instalar-tarjeta .mb-5 {
    margin-bottom: 12px !important;
  }

  .instalar-tarjeta .mt-5 {
    margin-top: 12px !important;
  }
}

/* Meseros: violeta. */
.bg_install--meseros {
  background-color: #1a1740 !important;
  background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1.5px),
    radial-gradient(circle at 12% 18%, rgba(76, 175, 80, 0.1), transparent 42%),
    radial-gradient(circle at 88% 85%, rgba(149, 117, 205, 0.42), transparent 52%),
    linear-gradient(135deg, #17133a 0%, #272163 50%, #372d83 100%) !important;
}

.v-application .bg_install--meseros .instalar-titulo {
  color: #5e35b1 !important;
}

.bg_install--meseros .instalar-icono {
  background: rgba(94, 53, 177, 0.12);
}

.v-application .bg_install--meseros .instalar-icono .v-icon {
  color: #5e35b1 !important;
}

/* Cocina: rojo vino. */
.bg_install--cocina {
  background-color: #2b0b16 !important;
  background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1.5px),
    radial-gradient(circle at 12% 18%, rgba(76, 175, 80, 0.08), transparent 42%),
    radial-gradient(circle at 88% 85%, rgba(224, 82, 120, 0.4), transparent 52%),
    linear-gradient(135deg, #2a0a15 0%, #4a1428 50%, #6a1c38 100%) !important;
}

.v-application .bg_install--cocina .instalar-titulo {
  color: #a31d48 !important;
}

.bg_install--cocina .instalar-icono {
  background: rgba(163, 29, 72, 0.12);
}

.v-application .bg_install--cocina .instalar-icono .v-icon {
  color: #a31d48 !important;
}

/* Casi opaca, como la del login. */
.auth-glass.instalar-tarjeta {
  background: rgba(255, 255, 255, 0.96) !important;
}
</style>
